/**
 * EchartsVizSpecV1 — typed intent shape for `reactive_visualization` chart authoring.
 *
 * Vendored mirror of `atlas-hub/services/hub/src/ai/echartsSpec.ts`. The CI
 * parity check (`echartsSpecParity.test.ts`) blocks merges that drift between
 * this file and the hub canonical, after stripping import-only and
 * canonicalSpecHash-only differences.
 *
 * The spec is structure-only — rows are read at render time from the node's
 * input cache and are never persisted into the spec. See ADR-0079.
 *
 * Browser-side note: `canonicalSpecHash` (hub-only, uses `node:crypto`) is
 * intentionally absent from this file. The browser does not need to compute
 * `priorSpecHash` — the hub validates patches.
 */

// ── Common shared shapes ────────────────────────────────────────────────────

export type ValueFormat = {
  kind: "number" | "currency" | "percent" | "date";
  precision?: number;
  compact?: boolean;
  currencyCode?: string;
  dateFormat?: string;
};

export type CommonOptions = {
  title?: string;
  subtitle?: string;
  legend?: { show?: boolean; position?: "top" | "bottom" | "left" | "right" };
  tooltip?: {
    show?: boolean;
    hideNulls?: boolean;
    hideZeros?: boolean;
    stackTotals?: boolean;
    stackTotalLabels?: Record<string, string>;
    /**
     * Date-format hint for the tooltip header (cartesian axis-trigger only).
     * Same syntax as `ValueFormat.dateFormat` (e.g. "MMM DD, YYYY"). Independent
     * of `encoding.x.format`, which only affects axis tick labels.
     */
    dateFormat?: string;
  };
  color?: {
    strategy: "atlas-default" | "categorical" | "sequential";
    palette?: string[];
  };
};

// ── Top-level discriminated union ───────────────────────────────────────────

export type EchartsVizSpecV1 =
  | CartesianSpec
  | PieSpec
  | GaugeSpec
  | FunnelSpec
  | TreemapSpec
  | CandlestickSpec
  | BoxplotSpec
  | RadarSpec
  | SankeySpec
  | HeatmapSpec;

// NOTE: SpecBase must NOT declare `options` — every variant declares its own
// `options: CommonOptions & {…}`. When both declared it, ts-json-schema-generator
// (with additionalProperties: false) intersected the two declarations' allowed
// key sets, silently collapsing every variant's generated options schema to the
// CommonOptions keys — which made yAxis/innerRadiusRatio/levels/… unreachable
// through the change seam and through AI structured output, while the runtime
// validators (below) still accepted them.
type SpecBase = { specVersion: "echarts.v1" };

// ── Cartesian (line / bar / area / scatter) ─────────────────────────────────

export type CartesianSpec = SpecBase & {
  chartType: "line" | "bar" | "area" | "scatter";
  encoding: {
    x: {
      column: string;
      dataType: "category" | "time" | "value";
      format?: ValueFormat;
    };
    y?: Array<{
      column: string;
      dataType: "value";
      format?: ValueFormat;
      axisIndex?: 0 | 1;
      seriesType?: "line" | "bar" | "area" | "scatter";
      stack?: string;
    }>;
    series?: {
      column: string;
      valueColumn: string;
      seriesType?: "line" | "bar" | "area" | "scatter";
      stack?: string;
    };
  };
  options: CommonOptions & {
    yAxis?: { min?: number; max?: number; format?: ValueFormat };
    yAxisRight?: { min?: number; max?: number; format?: ValueFormat };
  };
};

export type PieSpec = SpecBase & {
  chartType: "pie" | "donut";
  encoding: {
    category: { column: string };
    value: { column: string; format?: ValueFormat };
  };
  options: CommonOptions & {
    innerRadiusRatio?: number;
    labelPosition?: "inside" | "outside" | "none";
  };
};

export type GaugeSpec = SpecBase & {
  chartType: "gauge";
  encoding: {
    value: { column: string; format?: ValueFormat };
    min?: number;
    max?: number;
  };
  options: CommonOptions & {
    thresholds?: Array<{ value: number; color: string; label?: string }>;
  };
};

export type FunnelSpec = SpecBase & {
  chartType: "funnel";
  encoding: {
    category: { column: string };
    value: { column: string; format?: ValueFormat };
  };
  options: CommonOptions & {
    sort?: "asc" | "desc" | "none";
    labelPosition?: "inside" | "outside";
  };
};

export type TreemapSpec = SpecBase & {
  chartType: "treemap" | "sunburst";
  encoding: {
    hierarchy: Array<{ column: string }>;
    value: { column: string; format?: ValueFormat };
  };
  options: CommonOptions & {
    levels?: number;
  };
};

export type CandlestickSpec = SpecBase & {
  chartType: "candlestick";
  encoding: {
    x: { column: string; dataType: "time" | "category"; format?: ValueFormat };
    ohlc: {
      open: string;
      close: string;
      low: string;
      high: string;
      format?: ValueFormat;
    };
  };
  options: CommonOptions & {
    yAxis?: { min?: number; max?: number; format?: ValueFormat };
  };
};

export type BoxplotSpec = SpecBase & {
  chartType: "boxplot";
  encoding: {
    x: { column: string; dataType: "category" | "time" };
    summary: {
      min: string;
      q1: string;
      median: string;
      q3: string;
      max: string;
      format?: ValueFormat;
    };
  };
  options: CommonOptions & {
    yAxis?: { min?: number; max?: number; format?: ValueFormat };
  };
};

export type RadarSpec = SpecBase & {
  chartType: "radar";
  encoding: {
    indicators: Array<{ column: string; min?: number; max?: number }>;
    series?: { column: string };
    valueFormat?: ValueFormat;
  };
  options: CommonOptions;
};

export type SankeySpec = SpecBase & {
  chartType: "sankey";
  encoding: {
    source: { column: string };
    target: { column: string };
    value: { column: string; format?: ValueFormat };
  };
  options: CommonOptions & {
    orient?: "horizontal" | "vertical";
  };
};

export type HeatmapSpec = SpecBase & {
  chartType: "heatmap";
  encoding: {
    x: { column: string; dataType: "category" | "time" };
    y: { column: string; dataType: "category" | "time" };
    value: { column: string; format?: ValueFormat };
  };
  options: CommonOptions & {
    visualMap?: { min?: number; max?: number; palette?: string[] };
  };
};

// ── Constants ───────────────────────────────────────────────────────────────

export const SUPPORTED_CHART_TYPES = [
  "line", "bar", "area", "scatter",
  "pie", "donut",
  "gauge", "funnel",
  "treemap", "sunburst",
  "candlestick", "boxplot",
  "radar", "sankey", "heatmap",
] as const;

const VALUE_FORMAT_KINDS = ["number", "currency", "percent", "date"] as const;
const COLOR_STRATEGIES = ["atlas-default", "categorical", "sequential"] as const;
const LEGEND_POSITIONS = ["top", "bottom", "left", "right"] as const;
const X_DATA_TYPES = ["category", "time", "value"] as const;
const SERIES_TYPES = ["line", "bar", "area", "scatter"] as const;
const AXIS_INDICES = [0, 1] as const;
const PIE_LABEL_POSITIONS = ["inside", "outside", "none"] as const;
const FUNNEL_LABEL_POSITIONS = ["inside", "outside"] as const;
const FUNNEL_SORTS = ["asc", "desc", "none"] as const;
const SANKEY_ORIENTS = ["horizontal", "vertical"] as const;

// ── Validation utilities ────────────────────────────────────────────────────

type Errors = string[];

function isPlainObject(x: unknown): x is Record<string, unknown> {
  return !!x && typeof x === "object" && !Array.isArray(x);
}

function checkNoUnknownKeys(
  input: Record<string, unknown>,
  knownKeys: readonly string[],
  path: string,
  errors: Errors,
): void {
  for (const key of Object.keys(input)) {
    if (!knownKeys.includes(key)) {
      errors.push(`${path}: unknown field "${key}"`);
    }
  }
}

function readString(
  input: Record<string, unknown>,
  key: string,
  path: string,
  errors: Errors,
  opts: { required?: boolean } = {},
): string | undefined {
  const value = input[key];
  if (value === undefined) {
    if (opts.required) errors.push(`${path}.${key}: required`);
    return undefined;
  }
  if (typeof value !== "string") {
    errors.push(`${path}.${key}: must be a string`);
    return undefined;
  }
  if (opts.required && value.trim().length === 0) {
    errors.push(`${path}.${key}: must be non-empty`);
    return undefined;
  }
  return value;
}

function readEnum<T extends string>(
  input: Record<string, unknown>,
  key: string,
  allowed: readonly T[],
  path: string,
  errors: Errors,
  opts: { required?: boolean } = {},
): T | undefined {
  const value = input[key];
  if (value === undefined) {
    if (opts.required) errors.push(`${path}.${key}: required`);
    return undefined;
  }
  if (typeof value !== "string" || !allowed.includes(value as T)) {
    errors.push(`${path}.${key}: must be one of ${allowed.map((v) => `"${v}"`).join(", ")}`);
    return undefined;
  }
  return value as T;
}

function readBoolean(
  input: Record<string, unknown>,
  key: string,
  path: string,
  errors: Errors,
): boolean | undefined {
  const value = input[key];
  if (value === undefined) return undefined;
  if (typeof value !== "boolean") {
    errors.push(`${path}.${key}: must be a boolean`);
    return undefined;
  }
  return value;
}

function readNumber(
  input: Record<string, unknown>,
  key: string,
  path: string,
  errors: Errors,
  opts: { required?: boolean; integer?: boolean; min?: number; max?: number } = {},
): number | undefined {
  const value = input[key];
  if (value === undefined) {
    if (opts.required) errors.push(`${path}.${key}: required`);
    return undefined;
  }
  if (typeof value !== "number" || !Number.isFinite(value)) {
    errors.push(`${path}.${key}: must be a finite number`);
    return undefined;
  }
  if (opts.integer && !Number.isInteger(value)) {
    errors.push(`${path}.${key}: must be an integer`);
    return undefined;
  }
  if (opts.min !== undefined && value < opts.min) {
    errors.push(`${path}.${key}: must be >= ${opts.min}`);
    return undefined;
  }
  if (opts.max !== undefined && value > opts.max) {
    errors.push(`${path}.${key}: must be <= ${opts.max}`);
    return undefined;
  }
  return value;
}

function readObject(
  input: Record<string, unknown>,
  key: string,
  path: string,
  errors: Errors,
  opts: { required?: boolean } = {},
): Record<string, unknown> | undefined {
  const value = input[key];
  if (value === undefined) {
    if (opts.required) errors.push(`${path}.${key}: required`);
    return undefined;
  }
  if (!isPlainObject(value)) {
    errors.push(`${path}.${key}: must be an object`);
    return undefined;
  }
  return value;
}

function readArray(
  input: Record<string, unknown>,
  key: string,
  path: string,
  errors: Errors,
  opts: { required?: boolean; minLength?: number } = {},
): unknown[] | undefined {
  const value = input[key];
  if (value === undefined) {
    if (opts.required) errors.push(`${path}.${key}: required`);
    return undefined;
  }
  if (!Array.isArray(value)) {
    errors.push(`${path}.${key}: must be an array`);
    return undefined;
  }
  if (opts.minLength !== undefined && value.length < opts.minLength) {
    errors.push(`${path}.${key}: must have at least ${opts.minLength} item(s)`);
    return undefined;
  }
  return value;
}

// ── Shared sub-validators ───────────────────────────────────────────────────

const VALUE_FORMAT_KEYS = ["kind", "precision", "compact", "currencyCode", "dateFormat"] as const;

function validateValueFormat(input: unknown, path: string, errors: Errors): ValueFormat | undefined {
  if (!isPlainObject(input)) {
    errors.push(`${path}: must be an object`);
    return undefined;
  }
  checkNoUnknownKeys(input, VALUE_FORMAT_KEYS, path, errors);
  const kind = readEnum(input, "kind", VALUE_FORMAT_KINDS, path, errors, { required: true });
  if (!kind) return undefined;
  const precision = readNumber(input, "precision", path, errors, { integer: true, min: 0, max: 10 });
  const compact = readBoolean(input, "compact", path, errors);
  const currencyCode = readString(input, "currencyCode", path, errors);
  if (currencyCode !== undefined && currencyCode.length !== 3) {
    errors.push(`${path}.currencyCode: must be a 3-letter ISO 4217 code`);
  }
  const dateFormat = readString(input, "dateFormat", path, errors);

  const out: ValueFormat = { kind };
  if (precision !== undefined) out.precision = precision;
  if (compact !== undefined) out.compact = compact;
  if (currencyCode !== undefined) out.currencyCode = currencyCode;
  if (dateFormat !== undefined) out.dateFormat = dateFormat;
  return out;
}

const TOOLTIP_KEYS = ["show", "hideNulls", "hideZeros", "stackTotals", "stackTotalLabels", "dateFormat"] as const;

function validateTooltip(input: unknown, path: string, errors: Errors): CommonOptions["tooltip"] {
  if (!isPlainObject(input)) {
    errors.push(`${path}: must be an object`);
    return undefined;
  }
  checkNoUnknownKeys(input, TOOLTIP_KEYS, path, errors);
  const show = readBoolean(input, "show", path, errors);
  const hideNulls = readBoolean(input, "hideNulls", path, errors);
  const hideZeros = readBoolean(input, "hideZeros", path, errors);
  const stackTotals = readBoolean(input, "stackTotals", path, errors);
  const dateFormat = readString(input, "dateFormat", path, errors);

  let stackTotalLabels: Record<string, string> | undefined;
  const labelsInput = input.stackTotalLabels;
  if (labelsInput !== undefined) {
    if (!isPlainObject(labelsInput)) {
      errors.push(`${path}.stackTotalLabels: must be an object`);
    } else {
      stackTotalLabels = {};
      for (const [k, v] of Object.entries(labelsInput)) {
        if (typeof v !== "string") {
          errors.push(`${path}.stackTotalLabels["${k}"]: must be a string`);
        } else {
          stackTotalLabels[k] = v;
        }
      }
    }
  }

  const out: NonNullable<CommonOptions["tooltip"]> = {};
  if (show !== undefined) out.show = show;
  if (hideNulls !== undefined) out.hideNulls = hideNulls;
  if (hideZeros !== undefined) out.hideZeros = hideZeros;
  if (stackTotals !== undefined) out.stackTotals = stackTotals;
  if (stackTotalLabels !== undefined) out.stackTotalLabels = stackTotalLabels;
  if (dateFormat !== undefined) out.dateFormat = dateFormat;
  return out;
}

const COLOR_KEYS = ["strategy", "palette"] as const;

function validateColor(input: unknown, path: string, errors: Errors): CommonOptions["color"] {
  if (!isPlainObject(input)) {
    errors.push(`${path}: must be an object`);
    return undefined;
  }
  checkNoUnknownKeys(input, COLOR_KEYS, path, errors);
  const strategy = readEnum(input, "strategy", COLOR_STRATEGIES, path, errors, { required: true });
  if (!strategy) return undefined;

  let palette: string[] | undefined;
  const paletteInput = input.palette;
  if (paletteInput !== undefined) {
    if (!Array.isArray(paletteInput)) {
      errors.push(`${path}.palette: must be an array of strings`);
    } else {
      palette = [];
      for (let i = 0; i < paletteInput.length; i++) {
        const v = paletteInput[i];
        if (typeof v !== "string") {
          errors.push(`${path}.palette[${i}]: must be a string`);
        } else {
          palette.push(v);
        }
      }
    }
  }

  const out: NonNullable<CommonOptions["color"]> = { strategy };
  if (palette !== undefined) out.palette = palette;
  return out;
}

const LEGEND_KEYS = ["show", "position"] as const;

function validateLegend(input: unknown, path: string, errors: Errors): CommonOptions["legend"] {
  if (!isPlainObject(input)) {
    errors.push(`${path}: must be an object`);
    return undefined;
  }
  checkNoUnknownKeys(input, LEGEND_KEYS, path, errors);
  const show = readBoolean(input, "show", path, errors);
  const position = readEnum(input, "position", LEGEND_POSITIONS, path, errors);
  const out: NonNullable<CommonOptions["legend"]> = {};
  if (show !== undefined) out.show = show;
  if (position !== undefined) out.position = position;
  return out;
}

/**
 * Validate the CommonOptions portion of a spec. Per-chart-type extensions
 * (yAxis, innerRadiusRatio, etc.) are applied after this in the variant
 * validator.
 */
const COMMON_OPTIONS_KEYS = ["title", "subtitle", "legend", "tooltip", "color"] as const;

function validateCommonOptionsInto(
  input: Record<string, unknown>,
  out: Record<string, unknown>,
  path: string,
  errors: Errors,
): void {
  const title = readString(input, "title", path, errors);
  const subtitle = readString(input, "subtitle", path, errors);
  if (title !== undefined) out.title = title;
  if (subtitle !== undefined) out.subtitle = subtitle;
  if (input.legend !== undefined) {
    const legend = validateLegend(input.legend, `${path}.legend`, errors);
    if (legend !== undefined) out.legend = legend;
  }
  if (input.tooltip !== undefined) {
    const tooltip = validateTooltip(input.tooltip, `${path}.tooltip`, errors);
    if (tooltip !== undefined) out.tooltip = tooltip;
  }
  if (input.color !== undefined) {
    const color = validateColor(input.color, `${path}.color`, errors);
    if (color !== undefined) out.color = color;
  }
}

const Y_AXIS_OPTIONS_KEYS = ["min", "max", "format"] as const;

function validateYAxisOptions(
  input: unknown,
  path: string,
  errors: Errors,
): { min?: number; max?: number; format?: ValueFormat } | undefined {
  if (!isPlainObject(input)) {
    errors.push(`${path}: must be an object`);
    return undefined;
  }
  checkNoUnknownKeys(input, Y_AXIS_OPTIONS_KEYS, path, errors);
  const min = readNumber(input, "min", path, errors);
  const max = readNumber(input, "max", path, errors);
  let format: ValueFormat | undefined;
  if (input.format !== undefined) {
    format = validateValueFormat(input.format, `${path}.format`, errors);
  }
  const out: { min?: number; max?: number; format?: ValueFormat } = {};
  if (min !== undefined) out.min = min;
  if (max !== undefined) out.max = max;
  if (format !== undefined) out.format = format;
  return out;
}

const COLUMN_REF_KEYS = ["column"] as const;

function validateColumnRef(
  input: unknown,
  path: string,
  errors: Errors,
): { column: string } | undefined {
  if (!isPlainObject(input)) {
    errors.push(`${path}: must be an object`);
    return undefined;
  }
  checkNoUnknownKeys(input, COLUMN_REF_KEYS, path, errors);
  const column = readString(input, "column", path, errors, { required: true });
  if (column === undefined) return undefined;
  return { column };
}

const VALUE_REF_KEYS = ["column", "format"] as const;

function validateValueRef(
  input: unknown,
  path: string,
  errors: Errors,
): { column: string; format?: ValueFormat } | undefined {
  if (!isPlainObject(input)) {
    errors.push(`${path}: must be an object`);
    return undefined;
  }
  checkNoUnknownKeys(input, VALUE_REF_KEYS, path, errors);
  const column = readString(input, "column", path, errors, { required: true });
  if (column === undefined) return undefined;
  const out: { column: string; format?: ValueFormat } = { column };
  if (input.format !== undefined) {
    const format = validateValueFormat(input.format, `${path}.format`, errors);
    if (format !== undefined) out.format = format;
  }
  return out;
}

const X_ENCODING_KEYS = ["column", "dataType", "format"] as const;

function validateXEncoding(
  input: unknown,
  path: string,
  errors: Errors,
  allowedDataTypes: readonly ("category" | "time" | "value")[] = X_DATA_TYPES,
):
  | { column: string; dataType: "category" | "time" | "value"; format?: ValueFormat }
  | undefined {
  if (!isPlainObject(input)) {
    errors.push(`${path}: must be an object`);
    return undefined;
  }
  checkNoUnknownKeys(input, X_ENCODING_KEYS, path, errors);
  const column = readString(input, "column", path, errors, { required: true });
  const dataType = readEnum(input, "dataType", allowedDataTypes, path, errors, { required: true });
  if (!column || !dataType) return undefined;
  const out: { column: string; dataType: "category" | "time" | "value"; format?: ValueFormat } = {
    column,
    dataType,
  };
  if (input.format !== undefined) {
    const format = validateValueFormat(input.format, `${path}.format`, errors);
    if (format !== undefined) out.format = format;
  }
  return out;
}

// ── Per-chart-type variant validators ───────────────────────────────────────

const CARTESIAN_Y_KEYS = ["column", "dataType", "format", "axisIndex", "seriesType", "stack"] as const;
const CARTESIAN_SERIES_KEYS = ["column", "valueColumn", "seriesType", "stack"] as const;
const CARTESIAN_ENCODING_KEYS = ["x", "y", "series"] as const;
const CARTESIAN_OPTIONS_KEYS = [...COMMON_OPTIONS_KEYS, "yAxis", "yAxisRight"] as const;
const SPEC_TOP_KEYS = ["specVersion", "chartType", "encoding", "options"] as const;

function validateCartesian(input: Record<string, unknown>, path: string, errors: Errors): CartesianSpec | undefined {
  const chartType = input.chartType as CartesianSpec["chartType"];

  // encoding
  const encInput = readObject(input, "encoding", path, errors, { required: true });
  if (!encInput) return undefined;
  checkNoUnknownKeys(encInput, CARTESIAN_ENCODING_KEYS, `${path}.encoding`, errors);
  const x = validateXEncoding(encInput.x, `${path}.encoding.x`, errors);
  if (!x) return undefined;

  const hasY = encInput.y !== undefined;
  const hasSeries = encInput.series !== undefined;
  if (!hasY && !hasSeries) {
    errors.push(`${path}.encoding: must set either "y" (wide form) or "series" (long form)`);
    return undefined;
  }
  if (hasY && hasSeries) {
    errors.push(`${path}.encoding: cannot set both "y" and "series" — choose wide or long form`);
    return undefined;
  }

  let y: CartesianSpec["encoding"]["y"];
  if (hasY) {
    const yArr = readArray(encInput, "y", `${path}.encoding`, errors, { minLength: 1 });
    if (!yArr) return undefined;
    y = [];
    for (let i = 0; i < yArr.length; i++) {
      const item = yArr[i];
      const yPath = `${path}.encoding.y[${i}]`;
      if (!isPlainObject(item)) {
        errors.push(`${yPath}: must be an object`);
        continue;
      }
      checkNoUnknownKeys(item, CARTESIAN_Y_KEYS, yPath, errors);
      const column = readString(item, "column", yPath, errors, { required: true });
      const dataType = readEnum(item, "dataType", ["value"] as const, yPath, errors, { required: true });
      if (!column || !dataType) continue;
      const yEntry: NonNullable<CartesianSpec["encoding"]["y"]>[number] = { column, dataType };
      if (item.format !== undefined) {
        const format = validateValueFormat(item.format, `${yPath}.format`, errors);
        if (format !== undefined) yEntry.format = format;
      }
      const axisIndex = readNumber(item, "axisIndex", yPath, errors, { integer: true, min: 0, max: 1 });
      if (axisIndex !== undefined) yEntry.axisIndex = axisIndex as 0 | 1;
      const seriesType = readEnum(item, "seriesType", SERIES_TYPES, yPath, errors);
      if (seriesType !== undefined) yEntry.seriesType = seriesType;
      const stack = readString(item, "stack", yPath, errors);
      if (stack !== undefined) yEntry.stack = stack;
      y.push(yEntry);
    }
    if (y.length === 0) return undefined;
  }

  let series: CartesianSpec["encoding"]["series"];
  if (hasSeries) {
    const seriesObj = readObject(encInput, "series", `${path}.encoding`, errors, { required: true });
    if (!seriesObj) return undefined;
    checkNoUnknownKeys(seriesObj, CARTESIAN_SERIES_KEYS, `${path}.encoding.series`, errors);
    const column = readString(seriesObj, "column", `${path}.encoding.series`, errors, { required: true });
    const valueColumn = readString(seriesObj, "valueColumn", `${path}.encoding.series`, errors, { required: true });
    if (!column || !valueColumn) return undefined;
    series = { column, valueColumn };
    const seriesType = readEnum(seriesObj, "seriesType", SERIES_TYPES, `${path}.encoding.series`, errors);
    if (seriesType !== undefined) series.seriesType = seriesType;
    const stack = readString(seriesObj, "stack", `${path}.encoding.series`, errors);
    if (stack !== undefined) series.stack = stack;
  }

  // options (common + cartesian extensions)
  const optInput = readObject(input, "options", path, errors, { required: true });
  if (!optInput) return undefined;
  checkNoUnknownKeys(optInput, CARTESIAN_OPTIONS_KEYS, `${path}.options`, errors);
  const options: Record<string, unknown> = {};
  validateCommonOptionsInto(optInput, options, `${path}.options`, errors);
  if (optInput.yAxis !== undefined) {
    const yAxis = validateYAxisOptions(optInput.yAxis, `${path}.options.yAxis`, errors);
    if (yAxis !== undefined) options.yAxis = yAxis;
  }
  if (optInput.yAxisRight !== undefined) {
    const yAxisRight = validateYAxisOptions(optInput.yAxisRight, `${path}.options.yAxisRight`, errors);
    if (yAxisRight !== undefined) options.yAxisRight = yAxisRight;
  }

  if (errors.length > 0) return undefined;

  const encoding: CartesianSpec["encoding"] = { x };
  if (y) encoding.y = y;
  if (series) encoding.series = series;

  return {
    specVersion: "echarts.v1",
    chartType,
    encoding,
    options: options as CartesianSpec["options"],
  };
}

const PIE_OPTIONS_KEYS = [...COMMON_OPTIONS_KEYS, "innerRadiusRatio", "labelPosition"] as const;
const PIE_ENCODING_KEYS = ["category", "value"] as const;

function validatePie(input: Record<string, unknown>, path: string, errors: Errors): PieSpec | undefined {
  const chartType = input.chartType as PieSpec["chartType"];

  const encInput = readObject(input, "encoding", path, errors, { required: true });
  if (!encInput) return undefined;
  checkNoUnknownKeys(encInput, PIE_ENCODING_KEYS, `${path}.encoding`, errors);
  const category = validateColumnRef(encInput.category, `${path}.encoding.category`, errors);
  const value = validateValueRef(encInput.value, `${path}.encoding.value`, errors);
  if (!category || !value) return undefined;

  const optInput = readObject(input, "options", path, errors, { required: true });
  if (!optInput) return undefined;
  checkNoUnknownKeys(optInput, PIE_OPTIONS_KEYS, `${path}.options`, errors);
  const options: Record<string, unknown> = {};
  validateCommonOptionsInto(optInput, options, `${path}.options`, errors);
  const innerRadiusRatio = readNumber(optInput, "innerRadiusRatio", `${path}.options`, errors, { min: 0, max: 0.95 });
  if (innerRadiusRatio !== undefined) options.innerRadiusRatio = innerRadiusRatio;
  const labelPosition = readEnum(optInput, "labelPosition", PIE_LABEL_POSITIONS, `${path}.options`, errors);
  if (labelPosition !== undefined) options.labelPosition = labelPosition;

  if (errors.length > 0) return undefined;

  return {
    specVersion: "echarts.v1",
    chartType,
    encoding: { category, value },
    options: options as PieSpec["options"],
  };
}

const GAUGE_ENCODING_KEYS = ["value", "min", "max"] as const;
const GAUGE_OPTIONS_KEYS = [...COMMON_OPTIONS_KEYS, "thresholds"] as const;
const GAUGE_THRESHOLD_KEYS = ["value", "color", "label"] as const;

function validateGauge(input: Record<string, unknown>, path: string, errors: Errors): GaugeSpec | undefined {
  const encInput = readObject(input, "encoding", path, errors, { required: true });
  if (!encInput) return undefined;
  checkNoUnknownKeys(encInput, GAUGE_ENCODING_KEYS, `${path}.encoding`, errors);
  const value = validateValueRef(encInput.value, `${path}.encoding.value`, errors);
  if (!value) return undefined;
  const min = readNumber(encInput, "min", `${path}.encoding`, errors);
  const max = readNumber(encInput, "max", `${path}.encoding`, errors, { required: true });
  if (max === undefined) return undefined;

  const optInput = readObject(input, "options", path, errors, { required: true });
  if (!optInput) return undefined;
  checkNoUnknownKeys(optInput, GAUGE_OPTIONS_KEYS, `${path}.options`, errors);
  const options: Record<string, unknown> = {};
  validateCommonOptionsInto(optInput, options, `${path}.options`, errors);

  let thresholds: GaugeSpec["options"]["thresholds"];
  if (optInput.thresholds !== undefined) {
    const thrArr = readArray(optInput, "thresholds", `${path}.options`, errors);
    if (thrArr) {
      thresholds = [];
      for (let i = 0; i < thrArr.length; i++) {
        const item = thrArr[i];
        const tp = `${path}.options.thresholds[${i}]`;
        if (!isPlainObject(item)) {
          errors.push(`${tp}: must be an object`);
          continue;
        }
        checkNoUnknownKeys(item, GAUGE_THRESHOLD_KEYS, tp, errors);
        const tValue = readNumber(item, "value", tp, errors, { required: true });
        const color = readString(item, "color", tp, errors, { required: true });
        const label = readString(item, "label", tp, errors);
        if (tValue === undefined || color === undefined) continue;
        const t: NonNullable<GaugeSpec["options"]["thresholds"]>[number] = { value: tValue, color };
        if (label !== undefined) t.label = label;
        thresholds.push(t);
      }
    }
  }
  if (thresholds !== undefined) options.thresholds = thresholds;

  if (errors.length > 0) return undefined;

  const encoding: GaugeSpec["encoding"] = { value, max };
  if (min !== undefined) encoding.min = min;

  return {
    specVersion: "echarts.v1",
    chartType: "gauge",
    encoding,
    options: options as GaugeSpec["options"],
  };
}

const FUNNEL_OPTIONS_KEYS = [...COMMON_OPTIONS_KEYS, "sort", "labelPosition"] as const;
const FUNNEL_ENCODING_KEYS = ["category", "value"] as const;

function validateFunnel(input: Record<string, unknown>, path: string, errors: Errors): FunnelSpec | undefined {
  const encInput = readObject(input, "encoding", path, errors, { required: true });
  if (!encInput) return undefined;
  checkNoUnknownKeys(encInput, FUNNEL_ENCODING_KEYS, `${path}.encoding`, errors);
  const category = validateColumnRef(encInput.category, `${path}.encoding.category`, errors);
  const value = validateValueRef(encInput.value, `${path}.encoding.value`, errors);
  if (!category || !value) return undefined;

  const optInput = readObject(input, "options", path, errors, { required: true });
  if (!optInput) return undefined;
  checkNoUnknownKeys(optInput, FUNNEL_OPTIONS_KEYS, `${path}.options`, errors);
  const options: Record<string, unknown> = {};
  validateCommonOptionsInto(optInput, options, `${path}.options`, errors);
  const sort = readEnum(optInput, "sort", FUNNEL_SORTS, `${path}.options`, errors);
  if (sort !== undefined) options.sort = sort;
  const labelPosition = readEnum(optInput, "labelPosition", FUNNEL_LABEL_POSITIONS, `${path}.options`, errors);
  if (labelPosition !== undefined) options.labelPosition = labelPosition;

  if (errors.length > 0) return undefined;

  return {
    specVersion: "echarts.v1",
    chartType: "funnel",
    encoding: { category, value },
    options: options as FunnelSpec["options"],
  };
}

const TREEMAP_ENCODING_KEYS = ["hierarchy", "value"] as const;
const TREEMAP_OPTIONS_KEYS = [...COMMON_OPTIONS_KEYS, "levels"] as const;

function validateTreemap(input: Record<string, unknown>, path: string, errors: Errors): TreemapSpec | undefined {
  const chartType = input.chartType as TreemapSpec["chartType"];

  const encInput = readObject(input, "encoding", path, errors, { required: true });
  if (!encInput) return undefined;
  checkNoUnknownKeys(encInput, TREEMAP_ENCODING_KEYS, `${path}.encoding`, errors);

  const hierArr = readArray(encInput, "hierarchy", `${path}.encoding`, errors, { minLength: 1 });
  if (!hierArr) return undefined;
  const hierarchy: Array<{ column: string }> = [];
  for (let i = 0; i < hierArr.length; i++) {
    const ref = validateColumnRef(hierArr[i], `${path}.encoding.hierarchy[${i}]`, errors);
    if (ref) hierarchy.push(ref);
  }
  if (hierarchy.length !== hierArr.length) return undefined;

  const value = validateValueRef(encInput.value, `${path}.encoding.value`, errors);
  if (!value) return undefined;

  const optInput = readObject(input, "options", path, errors, { required: true });
  if (!optInput) return undefined;
  checkNoUnknownKeys(optInput, TREEMAP_OPTIONS_KEYS, `${path}.options`, errors);
  const options: Record<string, unknown> = {};
  validateCommonOptionsInto(optInput, options, `${path}.options`, errors);
  const levels = readNumber(optInput, "levels", `${path}.options`, errors, { integer: true, min: 1, max: 10 });
  if (levels !== undefined) options.levels = levels;

  if (errors.length > 0) return undefined;

  return {
    specVersion: "echarts.v1",
    chartType,
    encoding: { hierarchy, value },
    options: options as TreemapSpec["options"],
  };
}

const CANDLESTICK_ENCODING_KEYS = ["x", "ohlc"] as const;
const CANDLESTICK_OHLC_KEYS = ["open", "close", "low", "high", "format"] as const;
const CANDLESTICK_OPTIONS_KEYS = [...COMMON_OPTIONS_KEYS, "yAxis"] as const;

function validateCandlestick(input: Record<string, unknown>, path: string, errors: Errors): CandlestickSpec | undefined {
  const encInput = readObject(input, "encoding", path, errors, { required: true });
  if (!encInput) return undefined;
  checkNoUnknownKeys(encInput, CANDLESTICK_ENCODING_KEYS, `${path}.encoding`, errors);
  const x = validateXEncoding(encInput.x, `${path}.encoding.x`, errors, ["time", "category"] as const);
  if (!x) return undefined;

  const ohlcInput = readObject(encInput, "ohlc", `${path}.encoding`, errors, { required: true });
  if (!ohlcInput) return undefined;
  checkNoUnknownKeys(ohlcInput, CANDLESTICK_OHLC_KEYS, `${path}.encoding.ohlc`, errors);
  const open = readString(ohlcInput, "open", `${path}.encoding.ohlc`, errors, { required: true });
  const close = readString(ohlcInput, "close", `${path}.encoding.ohlc`, errors, { required: true });
  const low = readString(ohlcInput, "low", `${path}.encoding.ohlc`, errors, { required: true });
  const high = readString(ohlcInput, "high", `${path}.encoding.ohlc`, errors, { required: true });
  if (!open || !close || !low || !high) return undefined;
  const ohlc: CandlestickSpec["encoding"]["ohlc"] = { open, close, low, high };
  if (ohlcInput.format !== undefined) {
    const format = validateValueFormat(ohlcInput.format, `${path}.encoding.ohlc.format`, errors);
    if (format !== undefined) ohlc.format = format;
  }

  const optInput = readObject(input, "options", path, errors, { required: true });
  if (!optInput) return undefined;
  checkNoUnknownKeys(optInput, CANDLESTICK_OPTIONS_KEYS, `${path}.options`, errors);
  const options: Record<string, unknown> = {};
  validateCommonOptionsInto(optInput, options, `${path}.options`, errors);
  if (optInput.yAxis !== undefined) {
    const yAxis = validateYAxisOptions(optInput.yAxis, `${path}.options.yAxis`, errors);
    if (yAxis !== undefined) options.yAxis = yAxis;
  }

  if (errors.length > 0) return undefined;

  return {
    specVersion: "echarts.v1",
    chartType: "candlestick",
    encoding: { x: x as CandlestickSpec["encoding"]["x"], ohlc },
    options: options as CandlestickSpec["options"],
  };
}

const BOXPLOT_ENCODING_KEYS = ["x", "summary"] as const;
const BOXPLOT_SUMMARY_KEYS = ["min", "q1", "median", "q3", "max", "format"] as const;
const BOXPLOT_OPTIONS_KEYS = [...COMMON_OPTIONS_KEYS, "yAxis"] as const;

function validateBoxplot(input: Record<string, unknown>, path: string, errors: Errors): BoxplotSpec | undefined {
  const encInput = readObject(input, "encoding", path, errors, { required: true });
  if (!encInput) return undefined;
  checkNoUnknownKeys(encInput, BOXPLOT_ENCODING_KEYS, `${path}.encoding`, errors);
  const x = validateXEncoding(encInput.x, `${path}.encoding.x`, errors, ["category", "time"] as const);
  if (!x) return undefined;

  const summaryInput = readObject(encInput, "summary", `${path}.encoding`, errors, { required: true });
  if (!summaryInput) return undefined;
  checkNoUnknownKeys(summaryInput, BOXPLOT_SUMMARY_KEYS, `${path}.encoding.summary`, errors);
  const min = readString(summaryInput, "min", `${path}.encoding.summary`, errors, { required: true });
  const q1 = readString(summaryInput, "q1", `${path}.encoding.summary`, errors, { required: true });
  const median = readString(summaryInput, "median", `${path}.encoding.summary`, errors, { required: true });
  const q3 = readString(summaryInput, "q3", `${path}.encoding.summary`, errors, { required: true });
  const max = readString(summaryInput, "max", `${path}.encoding.summary`, errors, { required: true });
  if (!min || !q1 || !median || !q3 || !max) return undefined;
  const summary: BoxplotSpec["encoding"]["summary"] = { min, q1, median, q3, max };
  if (summaryInput.format !== undefined) {
    const format = validateValueFormat(summaryInput.format, `${path}.encoding.summary.format`, errors);
    if (format !== undefined) summary.format = format;
  }

  const optInput = readObject(input, "options", path, errors, { required: true });
  if (!optInput) return undefined;
  checkNoUnknownKeys(optInput, BOXPLOT_OPTIONS_KEYS, `${path}.options`, errors);
  const options: Record<string, unknown> = {};
  validateCommonOptionsInto(optInput, options, `${path}.options`, errors);
  if (optInput.yAxis !== undefined) {
    const yAxis = validateYAxisOptions(optInput.yAxis, `${path}.options.yAxis`, errors);
    if (yAxis !== undefined) options.yAxis = yAxis;
  }

  if (errors.length > 0) return undefined;

  return {
    specVersion: "echarts.v1",
    chartType: "boxplot",
    encoding: { x: x as BoxplotSpec["encoding"]["x"], summary },
    options: options as BoxplotSpec["options"],
  };
}

const RADAR_ENCODING_KEYS = ["indicators", "series", "valueFormat"] as const;
const RADAR_INDICATOR_KEYS = ["column", "min", "max"] as const;

function validateRadar(input: Record<string, unknown>, path: string, errors: Errors): RadarSpec | undefined {
  const encInput = readObject(input, "encoding", path, errors, { required: true });
  if (!encInput) return undefined;
  checkNoUnknownKeys(encInput, RADAR_ENCODING_KEYS, `${path}.encoding`, errors);

  const indArr = readArray(encInput, "indicators", `${path}.encoding`, errors, { minLength: 3 });
  if (!indArr) return undefined;
  const indicators: RadarSpec["encoding"]["indicators"] = [];
  for (let i = 0; i < indArr.length; i++) {
    const item = indArr[i];
    const ip = `${path}.encoding.indicators[${i}]`;
    if (!isPlainObject(item)) {
      errors.push(`${ip}: must be an object`);
      continue;
    }
    checkNoUnknownKeys(item, RADAR_INDICATOR_KEYS, ip, errors);
    const column = readString(item, "column", ip, errors, { required: true });
    if (!column) continue;
    const ind: RadarSpec["encoding"]["indicators"][number] = { column };
    const min = readNumber(item, "min", ip, errors);
    const max = readNumber(item, "max", ip, errors);
    if (min !== undefined) ind.min = min;
    if (max !== undefined) ind.max = max;
    indicators.push(ind);
  }
  if (indicators.length !== indArr.length) return undefined;

  const encoding: RadarSpec["encoding"] = { indicators };
  if (encInput.series !== undefined) {
    const series = validateColumnRef(encInput.series, `${path}.encoding.series`, errors);
    if (series) encoding.series = series;
  }
  if (encInput.valueFormat !== undefined) {
    const valueFormat = validateValueFormat(encInput.valueFormat, `${path}.encoding.valueFormat`, errors);
    if (valueFormat !== undefined) encoding.valueFormat = valueFormat;
  }

  const optInput = readObject(input, "options", path, errors, { required: true });
  if (!optInput) return undefined;
  checkNoUnknownKeys(optInput, COMMON_OPTIONS_KEYS, `${path}.options`, errors);
  const options: Record<string, unknown> = {};
  validateCommonOptionsInto(optInput, options, `${path}.options`, errors);

  if (errors.length > 0) return undefined;

  return {
    specVersion: "echarts.v1",
    chartType: "radar",
    encoding,
    options: options as RadarSpec["options"],
  };
}

const SANKEY_ENCODING_KEYS = ["source", "target", "value"] as const;
const SANKEY_OPTIONS_KEYS = [...COMMON_OPTIONS_KEYS, "orient"] as const;

function validateSankey(input: Record<string, unknown>, path: string, errors: Errors): SankeySpec | undefined {
  const encInput = readObject(input, "encoding", path, errors, { required: true });
  if (!encInput) return undefined;
  checkNoUnknownKeys(encInput, SANKEY_ENCODING_KEYS, `${path}.encoding`, errors);
  const source = validateColumnRef(encInput.source, `${path}.encoding.source`, errors);
  const target = validateColumnRef(encInput.target, `${path}.encoding.target`, errors);
  const value = validateValueRef(encInput.value, `${path}.encoding.value`, errors);
  if (!source || !target || !value) return undefined;

  const optInput = readObject(input, "options", path, errors, { required: true });
  if (!optInput) return undefined;
  checkNoUnknownKeys(optInput, SANKEY_OPTIONS_KEYS, `${path}.options`, errors);
  const options: Record<string, unknown> = {};
  validateCommonOptionsInto(optInput, options, `${path}.options`, errors);
  const orient = readEnum(optInput, "orient", SANKEY_ORIENTS, `${path}.options`, errors);
  if (orient !== undefined) options.orient = orient;

  if (errors.length > 0) return undefined;

  return {
    specVersion: "echarts.v1",
    chartType: "sankey",
    encoding: { source, target, value },
    options: options as SankeySpec["options"],
  };
}

const HEATMAP_ENCODING_KEYS = ["x", "y", "value"] as const;
const HEATMAP_OPTIONS_KEYS = [...COMMON_OPTIONS_KEYS, "visualMap"] as const;
const HEATMAP_VISUAL_MAP_KEYS = ["min", "max", "palette"] as const;

function validateHeatmap(input: Record<string, unknown>, path: string, errors: Errors): HeatmapSpec | undefined {
  const encInput = readObject(input, "encoding", path, errors, { required: true });
  if (!encInput) return undefined;
  checkNoUnknownKeys(encInput, HEATMAP_ENCODING_KEYS, `${path}.encoding`, errors);
  const x = validateXEncoding(encInput.x, `${path}.encoding.x`, errors, ["category", "time"] as const);
  const y = validateXEncoding(encInput.y, `${path}.encoding.y`, errors, ["category", "time"] as const);
  const value = validateValueRef(encInput.value, `${path}.encoding.value`, errors);
  if (!x || !y || !value) return undefined;

  const optInput = readObject(input, "options", path, errors, { required: true });
  if (!optInput) return undefined;
  checkNoUnknownKeys(optInput, HEATMAP_OPTIONS_KEYS, `${path}.options`, errors);
  const options: Record<string, unknown> = {};
  validateCommonOptionsInto(optInput, options, `${path}.options`, errors);

  if (optInput.visualMap !== undefined) {
    const vm = optInput.visualMap;
    if (!isPlainObject(vm)) {
      errors.push(`${path}.options.visualMap: must be an object`);
    } else {
      checkNoUnknownKeys(vm, HEATMAP_VISUAL_MAP_KEYS, `${path}.options.visualMap`, errors);
      const min = readNumber(vm, "min", `${path}.options.visualMap`, errors);
      const max = readNumber(vm, "max", `${path}.options.visualMap`, errors);
      let palette: string[] | undefined;
      if (vm.palette !== undefined) {
        if (!Array.isArray(vm.palette)) {
          errors.push(`${path}.options.visualMap.palette: must be an array of strings`);
        } else {
          palette = [];
          for (let i = 0; i < vm.palette.length; i++) {
            const s = vm.palette[i];
            if (typeof s !== "string") {
              errors.push(`${path}.options.visualMap.palette[${i}]: must be a string`);
            } else {
              palette.push(s);
            }
          }
        }
      }
      const visualMap: NonNullable<HeatmapSpec["options"]["visualMap"]> = {};
      if (min !== undefined) visualMap.min = min;
      if (max !== undefined) visualMap.max = max;
      if (palette !== undefined) visualMap.palette = palette;
      options.visualMap = visualMap;
    }
  }

  if (errors.length > 0) return undefined;

  return {
    specVersion: "echarts.v1",
    chartType: "heatmap",
    encoding: {
      x: x as HeatmapSpec["encoding"]["x"],
      y: y as HeatmapSpec["encoding"]["y"],
      value,
    },
    options: options as HeatmapSpec["options"],
  };
}

// ── Top-level validator ─────────────────────────────────────────────────────

export type ValidationResult =
  | { ok: true; spec: EchartsVizSpecV1 }
  | { ok: false; errors: string[] };

export function validateEchartsSpec(input: unknown): ValidationResult {
  const errors: string[] = [];
  const path = "spec";

  if (!isPlainObject(input)) {
    return { ok: false, errors: [`${path}: must be an object`] };
  }
  checkNoUnknownKeys(input, SPEC_TOP_KEYS, path, errors);

  if (input.specVersion !== "echarts.v1") {
    errors.push(`${path}.specVersion: must be "echarts.v1"`);
    return { ok: false, errors };
  }

  const chartType = input.chartType;
  if (typeof chartType !== "string" || !SUPPORTED_CHART_TYPES.includes(chartType as never)) {
    errors.push(
      `${path}.chartType: must be one of ${SUPPORTED_CHART_TYPES.map((t) => `"${t}"`).join(", ")}`,
    );
    return { ok: false, errors };
  }

  let spec: EchartsVizSpecV1 | undefined;
  switch (chartType) {
    case "line":
    case "bar":
    case "area":
    case "scatter":
      spec = validateCartesian(input, path, errors);
      break;
    case "pie":
    case "donut":
      spec = validatePie(input, path, errors);
      break;
    case "gauge":
      spec = validateGauge(input, path, errors);
      break;
    case "funnel":
      spec = validateFunnel(input, path, errors);
      break;
    case "treemap":
    case "sunburst":
      spec = validateTreemap(input, path, errors);
      break;
    case "candlestick":
      spec = validateCandlestick(input, path, errors);
      break;
    case "boxplot":
      spec = validateBoxplot(input, path, errors);
      break;
    case "radar":
      spec = validateRadar(input, path, errors);
      break;
    case "sankey":
      spec = validateSankey(input, path, errors);
      break;
    case "heatmap":
      spec = validateHeatmap(input, path, errors);
      break;
  }

  if (errors.length > 0 || !spec) {
    return { ok: false, errors: errors.length > 0 ? errors : [`${path}: validation failed`] };
  }
  return { ok: true, spec };
}

// ── Semantic validation (column references against upstream schema) ─────────

/**
 * Check that every column referenced by the spec exists in the upstream
 * schema. Called after `validateEchartsSpec` succeeds, with the upstream
 * `DatasetBundle.columns`.
 */
export function validateEchartsSpecAgainstSchema(
  spec: EchartsVizSpecV1,
  columns: Array<{ name: string }>,
): { ok: true } | { ok: false; errors: string[] } {
  const available = new Set(columns.map((c) => c.name));
  const refs = collectColumnReferences(spec);
  const missing = refs.filter((r) => !available.has(r.column));
  if (missing.length === 0) return { ok: true };
  const availableList = columns.map((c) => c.name).join(", ");
  const errors = missing.map(
    (r) => `${r.path}: encoded column "${r.column}" not found in input data. Available: ${availableList || "(none)"}`,
  );
  return { ok: false, errors };
}

function collectColumnReferences(spec: EchartsVizSpecV1): Array<{ path: string; column: string }> {
  const refs: Array<{ path: string; column: string }> = [];
  switch (spec.chartType) {
    case "line":
    case "bar":
    case "area":
    case "scatter":
      refs.push({ path: "encoding.x.column", column: spec.encoding.x.column });
      if (spec.encoding.y) {
        spec.encoding.y.forEach((y, i) =>
          refs.push({ path: `encoding.y[${i}].column`, column: y.column }),
        );
      }
      if (spec.encoding.series) {
        refs.push({ path: "encoding.series.column", column: spec.encoding.series.column });
        refs.push({ path: "encoding.series.valueColumn", column: spec.encoding.series.valueColumn });
      }
      break;
    case "pie":
    case "donut":
    case "funnel":
      refs.push({ path: "encoding.category.column", column: spec.encoding.category.column });
      refs.push({ path: "encoding.value.column", column: spec.encoding.value.column });
      break;
    case "gauge":
      refs.push({ path: "encoding.value.column", column: spec.encoding.value.column });
      break;
    case "treemap":
    case "sunburst":
      spec.encoding.hierarchy.forEach((h, i) =>
        refs.push({ path: `encoding.hierarchy[${i}].column`, column: h.column }),
      );
      refs.push({ path: "encoding.value.column", column: spec.encoding.value.column });
      break;
    case "candlestick":
      refs.push({ path: "encoding.x.column", column: spec.encoding.x.column });
      refs.push({ path: "encoding.ohlc.open", column: spec.encoding.ohlc.open });
      refs.push({ path: "encoding.ohlc.close", column: spec.encoding.ohlc.close });
      refs.push({ path: "encoding.ohlc.low", column: spec.encoding.ohlc.low });
      refs.push({ path: "encoding.ohlc.high", column: spec.encoding.ohlc.high });
      break;
    case "boxplot":
      refs.push({ path: "encoding.x.column", column: spec.encoding.x.column });
      refs.push({ path: "encoding.summary.min", column: spec.encoding.summary.min });
      refs.push({ path: "encoding.summary.q1", column: spec.encoding.summary.q1 });
      refs.push({ path: "encoding.summary.median", column: spec.encoding.summary.median });
      refs.push({ path: "encoding.summary.q3", column: spec.encoding.summary.q3 });
      refs.push({ path: "encoding.summary.max", column: spec.encoding.summary.max });
      break;
    case "radar":
      spec.encoding.indicators.forEach((ind, i) =>
        refs.push({ path: `encoding.indicators[${i}].column`, column: ind.column }),
      );
      if (spec.encoding.series) {
        refs.push({ path: "encoding.series.column", column: spec.encoding.series.column });
      }
      break;
    case "sankey":
      refs.push({ path: "encoding.source.column", column: spec.encoding.source.column });
      refs.push({ path: "encoding.target.column", column: spec.encoding.target.column });
      refs.push({ path: "encoding.value.column", column: spec.encoding.value.column });
      break;
    case "heatmap":
      refs.push({ path: "encoding.x.column", column: spec.encoding.x.column });
      refs.push({ path: "encoding.y.column", column: spec.encoding.y.column });
      refs.push({ path: "encoding.value.column", column: spec.encoding.value.column });
      break;
  }
  return refs;
}

// ── Empty spec helpers ──────────────────────────────────────────────────────

/**
 * Minimal valid spec for the given chart type. Column names are placeholders
 * (`__placeholder__`); semantic validation against the upstream schema will
 * fail, but `validateEchartsSpec` will succeed. Used by tests, the inspector
 * empty state, and the AI tab's first-message scaffolding.
 */
export function emptyEchartsSpec(chartType: EchartsVizSpecV1["chartType"]): EchartsVizSpecV1 {
  const ph = "__placeholder__";
  const opts: CommonOptions = {};
  switch (chartType) {
    case "line":
    case "bar":
    case "area":
    case "scatter":
      return {
        specVersion: "echarts.v1",
        chartType,
        encoding: {
          x: { column: ph, dataType: "category" },
          y: [{ column: ph, dataType: "value" }],
        },
        options: opts,
      };
    case "pie":
    case "donut":
      return {
        specVersion: "echarts.v1",
        chartType,
        encoding: { category: { column: ph }, value: { column: ph } },
        options: opts,
      };
    case "gauge":
      return {
        specVersion: "echarts.v1",
        chartType,
        encoding: { value: { column: ph }, max: 100 },
        options: opts,
      };
    case "funnel":
      return {
        specVersion: "echarts.v1",
        chartType,
        encoding: { category: { column: ph }, value: { column: ph } },
        options: opts,
      };
    case "treemap":
    case "sunburst":
      return {
        specVersion: "echarts.v1",
        chartType,
        encoding: { hierarchy: [{ column: ph }], value: { column: ph } },
        options: opts,
      };
    case "candlestick":
      return {
        specVersion: "echarts.v1",
        chartType,
        encoding: {
          x: { column: ph, dataType: "time" },
          ohlc: { open: ph, close: ph, low: ph, high: ph },
        },
        options: opts,
      };
    case "boxplot":
      return {
        specVersion: "echarts.v1",
        chartType,
        encoding: {
          x: { column: ph, dataType: "category" },
          summary: { min: ph, q1: ph, median: ph, q3: ph, max: ph },
        },
        options: opts,
      };
    case "radar":
      return {
        specVersion: "echarts.v1",
        chartType,
        encoding: {
          indicators: [{ column: ph }, { column: ph }, { column: ph }],
        },
        options: opts,
      };
    case "sankey":
      return {
        specVersion: "echarts.v1",
        chartType,
        encoding: {
          source: { column: ph },
          target: { column: ph },
          value: { column: ph },
        },
        options: opts,
      };
    case "heatmap":
      return {
        specVersion: "echarts.v1",
        chartType,
        encoding: {
          x: { column: ph, dataType: "category" },
          y: { column: ph, dataType: "category" },
          value: { column: ph },
        },
        options: opts,
      };
  }
}

// ── Canonical hash ──────────────────────────────────────────────────────────

/**
 * Deterministic JSON serialization with sorted object keys. Used for the
 * `priorSpecHash` optimistic-concurrency check on patch-mode agent responses.
 */
export function canonicalJSONStringify(value: unknown): string {
  if (value === null || value === undefined) return "null";
  if (typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return "[" + value.map(canonicalJSONStringify).join(",") + "]";
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return (
    "{" +
    keys
      .filter((k) => obj[k] !== undefined)
      .map((k) => JSON.stringify(k) + ":" + canonicalJSONStringify(obj[k]))
      .join(",") +
    "}"
  );
}

// canonicalSpecHash is hub-only (uses node:crypto). See header comment.

// ── JSON-Patch (RFC 6902, restricted ops) ───────────────────────────────────

export type JsonPatchOp =
  | { op: "add"; path: string; value: unknown }
  | { op: "replace"; path: string; value: unknown }
  | { op: "remove"; path: string };

const ALLOWED_PATCH_OPS = ["add", "replace", "remove"] as const;

export function applyJsonPatch(
  prior: EchartsVizSpecV1,
  patch: unknown,
): { ok: true; next: EchartsVizSpecV1 } | { ok: false; errors: string[] } {
  if (!Array.isArray(patch)) {
    return { ok: false, errors: ["patch: must be an array of operations"] };
  }

  const errors: string[] = [];
  for (let i = 0; i < patch.length; i++) {
    const op = patch[i];
    if (!isPlainObject(op)) {
      errors.push(`patch[${i}]: must be an object`);
      continue;
    }
    const opName = op.op;
    if (typeof opName !== "string" || !ALLOWED_PATCH_OPS.includes(opName as never)) {
      errors.push(`patch[${i}].op: must be one of ${ALLOWED_PATCH_OPS.join(", ")} (got "${String(opName)}")`);
    }
    if (typeof op.path !== "string" || !op.path.startsWith("/")) {
      errors.push(`patch[${i}].path: must be a JSON Pointer starting with "/"`);
    }
    if ((opName === "add" || opName === "replace") && !("value" in op)) {
      errors.push(`patch[${i}].value: required for "${opName}"`);
    }
  }
  if (errors.length > 0) return { ok: false, errors };

  let working: unknown = JSON.parse(JSON.stringify(prior));
  for (let i = 0; i < patch.length; i++) {
    const op = patch[i] as JsonPatchOp;
    try {
      working = applyOp(working, op);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      return { ok: false, errors: [`patch[${i}]: ${msg}`] };
    }
  }

  const result = validateEchartsSpec(working);
  if (!result.ok) {
    return {
      ok: false,
      errors: result.errors.map((e) => `patch result: ${e}`),
    };
  }
  return { ok: true, next: result.spec };
}

function parsePointer(path: string): string[] {
  if (path === "") return [];
  if (!path.startsWith("/")) throw new Error(`invalid pointer "${path}"`);
  return path
    .slice(1)
    .split("/")
    .map((seg) => seg.replace(/~1/g, "/").replace(/~0/g, "~"));
}

function applyOp(target: unknown, op: JsonPatchOp): unknown {
  const segments = parsePointer(op.path);
  if (segments.length === 0) {
    if (op.op === "remove") {
      throw new Error(`cannot remove the entire document`);
    }
    return op.op === "add" || op.op === "replace" ? op.value : target;
  }

  // Walk to parent
  let parent: unknown = target;
  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i];
    if (Array.isArray(parent)) {
      const idx = Number(seg);
      if (!Number.isInteger(idx) || idx < 0 || idx >= parent.length) {
        throw new Error(`path segment "${seg}" is not a valid array index at "/${segments.slice(0, i + 1).join("/")}"`);
      }
      parent = parent[idx];
    } else if (isPlainObject(parent)) {
      if (!(seg in parent)) {
        throw new Error(`path segment "${seg}" not found at "/${segments.slice(0, i + 1).join("/")}"`);
      }
      parent = parent[seg];
    } else {
      throw new Error(`cannot traverse into non-object at "/${segments.slice(0, i).join("/")}"`);
    }
  }

  const lastSeg = segments[segments.length - 1];
  if (Array.isArray(parent)) {
    let idx: number;
    if (lastSeg === "-") {
      idx = parent.length;
    } else {
      idx = Number(lastSeg);
      if (!Number.isInteger(idx) || idx < 0) {
        throw new Error(`path segment "${lastSeg}" is not a valid array index`);
      }
    }
    if (op.op === "add") {
      if (idx > parent.length) throw new Error(`add index ${idx} exceeds array length ${parent.length}`);
      parent.splice(idx, 0, op.value);
    } else if (op.op === "replace") {
      if (idx >= parent.length) throw new Error(`replace index ${idx} out of bounds`);
      parent[idx] = op.value;
    } else if (op.op === "remove") {
      if (idx >= parent.length) throw new Error(`remove index ${idx} out of bounds`);
      parent.splice(idx, 1);
    }
  } else if (isPlainObject(parent)) {
    if (op.op === "add" || op.op === "replace") {
      parent[lastSeg] = op.value;
    } else if (op.op === "remove") {
      if (!(lastSeg in parent)) throw new Error(`remove key "${lastSeg}" not present`);
      delete parent[lastSeg];
    }
  } else {
    throw new Error(`cannot apply op at "${op.path}": parent is not array or object`);
  }

  return target;
}
